import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Truck, Shield, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const SPECIES_OPTIONS = [
  { value: "equine", label: "Horses & Equine" },
  { value: "canine", label: "Dogs & Canine" },
  { value: "feline", label: "Cats & Feline" },
  { value: "cattle", label: "Cattle & Livestock" },
  { value: "exotics", label: "Exotics" },
  { value: "others", label: "Farm Animals" }
];

const EQUIPMENT_OPTIONS = [
  "Standard Trailer",
  "Climate-Controlled",
  "Box Truck",
  "Air Transport Coordination",
  "Large Livestock Trailer",
  "Small Animal Carrier"
];

const TransporterOnboarding = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: "",
    license_number: "",
    license_state: "",
    insurance_provider: "",
    insurance_policy_number: "",
    species_served: [],
    regions_served: [],
    equipment_types: [],
    contact_phone: "",
    contact_email: user?.email || "",
    location_city: "",
    location_state: "",
    base_rate_per_mile: "",
    minimum_charge: "",
    bio: "",
    years_experience: "",
    welfare_pledge_accepted: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.welfare_pledge_accepted) {
      toast.error("Please accept the animal welfare pledge");
      return;
    }
    
    if (!formData.business_name) {
      toast.error("Business name is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        base_rate_per_mile: formData.base_rate_per_mile ? parseFloat(formData.base_rate_per_mile) : null,
        minimum_charge: formData.minimum_charge ? parseFloat(formData.minimum_charge) : null,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      };
      
      await api.post("/transporter/register", payload);
      toast.success("Registration submitted! Awaiting verification.");
      navigate("/dashboard/transporter");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
            <Truck className="w-8 h-8 text-[#3B82F6]" />
          </div>
          <h1 className="text-3xl text-[#EDEDED] mb-2">Become a Verified Transporter</h1>
          <p className="text-[#A1A1AA]">
            Join Breedling's trusted network of animal transporters
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? "bg-[#3B82F6] text-white" : "bg-[#27272A] text-[#71717A]"
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-[#3B82F6]" : "bg-[#27272A]"}`} />}
            </div>
          ))}
        </div>

        <div className="glass-card p-6">
          {/* Step 1: Business Info */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl text-[#EDEDED] mb-4">Business Information</h2>
              
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Business Name *</label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  className="input-minimal w-full"
                  placeholder="Your transport company name"
                  data-testid="business-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">City</label>
                  <input
                    type="text"
                    name="location_city"
                    value={formData.location_city}
                    onChange={handleChange}
                    className="input-minimal w-full"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">State</label>
                  <select
                    name="location_state"
                    value={formData.location_state}
                    onChange={handleChange}
                    className="input-minimal w-full"
                  >
                    <option value="">Select State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    className="input-minimal w-full"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Contact Email</label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    className="input-minimal w-full"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Years of Experience</label>
                <input
                  type="number"
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleChange}
                  className="input-minimal w-full"
                  placeholder="5"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Bio / Description</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="input-minimal w-full h-24 resize-none"
                  placeholder="Tell potential customers about your transport services..."
                />
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!formData.business_name}
                className="btn-gold w-full disabled:opacity-50"
                data-testid="next-step-btn"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Licensing & Services */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl text-[#EDEDED] mb-4">Licensing & Services</h2>
              
              <div className="glass-card p-4 border-[#3B82F6]/30">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-[#3B82F6]" />
                  <span className="text-[#EDEDED] font-medium">Licensing Information</span>
                </div>
                <p className="text-xs text-[#71717A] mb-4">
                  Providing license and insurance details helps build trust with customers
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">License Number</label>
                    <input
                      type="text"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleChange}
                      className="input-minimal w-full"
                      placeholder="DOT-123456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">License State</label>
                    <select
                      name="license_state"
                      value={formData.license_state}
                      onChange={handleChange}
                      className="input-minimal w-full"
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="glass-card p-4">
                <label className="block text-sm text-[#A1A1AA] mb-2">Insurance Provider</label>
                <input
                  type="text"
                  name="insurance_provider"
                  value={formData.insurance_provider}
                  onChange={handleChange}
                  className="input-minimal w-full mb-3"
                  placeholder="Insurance Company Name"
                />
                <label className="block text-sm text-[#A1A1AA] mb-2">Policy Number</label>
                <input
                  type="text"
                  name="insurance_policy_number"
                  value={formData.insurance_policy_number}
                  onChange={handleChange}
                  className="input-minimal w-full"
                  placeholder="Policy #"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-3">Species You Transport</label>
                <div className="grid grid-cols-2 gap-2">
                  {SPECIES_OPTIONS.map(species => (
                    <button
                      key={species.value}
                      type="button"
                      onClick={() => toggleArrayItem("species_served", species.value)}
                      className={`p-3 rounded-lg border text-sm text-left transition-colors ${
                        formData.species_served.includes(species.value)
                          ? "border-[#3B82F6] bg-[#3B82F6]/10 text-[#3B82F6]"
                          : "border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]"
                      }`}
                    >
                      {species.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-3">Equipment Types</label>
                <div className="flex flex-wrap gap-2">
                  {EQUIPMENT_OPTIONS.map(equipment => (
                    <button
                      key={equipment}
                      type="button"
                      onClick={() => toggleArrayItem("equipment_types", equipment)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.equipment_types.includes(equipment)
                          ? "bg-[#3B82F6] text-white"
                          : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
                      }`}
                    >
                      {equipment}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <button onClick={() => setStep(1)} className="btn-secondary flex-1">
                  Back
                </button>
                <button onClick={() => setStep(3)} className="btn-gold flex-1">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Service Areas & Pricing */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl text-[#EDEDED] mb-4">Service Areas & Pricing</h2>
              
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-[#3B82F6]" />
                  <label className="text-sm text-[#A1A1AA]">States You Service</label>
                </div>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
                  {US_STATES.map(state => (
                    <button
                      key={state}
                      type="button"
                      onClick={() => toggleArrayItem("regions_served", state)}
                      className={`p-2 rounded text-xs font-medium transition-colors ${
                        formData.regions_served.includes(state)
                          ? "bg-[#3B82F6] text-white"
                          : "bg-[#27272A] text-[#71717A] hover:bg-[#3F3F46]"
                      }`}
                    >
                      {state}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#52525B] mt-2">
                  Selected: {formData.regions_served.length} states
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Base Rate (per mile)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]">$</span>
                    <input
                      type="number"
                      name="base_rate_per_mile"
                      value={formData.base_rate_per_mile}
                      onChange={handleChange}
                      className="input-minimal w-full pl-7"
                      placeholder="2.50"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Minimum Charge</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]">$</span>
                    <input
                      type="number"
                      name="minimum_charge"
                      value={formData.minimum_charge}
                      onChange={handleChange}
                      className="input-minimal w-full pl-7"
                      placeholder="150.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="glass-card p-4 border-[#22C55E]/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="welfare_pledge_accepted"
                    checked={formData.welfare_pledge_accepted}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 rounded border-[#3F3F46] bg-[#18181B] text-[#22C55E] focus:ring-[#22C55E]"
                    data-testid="welfare-pledge-checkbox"
                  />
                  <div>
                    <span className="text-[#EDEDED] font-medium">Animal Welfare Pledge</span>
                    <p className="text-xs text-[#71717A] mt-1">
                      I pledge to maintain the highest standards of animal care during transport, 
                      including proper ventilation, temperature control, regular rest stops, 
                      and humane handling practices.
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-4">
                <button onClick={() => setStep(2)} className="btn-secondary flex-1">
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.welfare_pledge_accepted}
                  className="btn-gold flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-registration-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                  Submit Registration
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 glass-card p-4 border-[#3B82F6]/20">
          <p className="text-xs text-[#71717A]">
            <strong className="text-[#A1A1AA]">What happens next?</strong> Our team will review your application 
            within 24-48 hours. Once verified, your profile will be visible to Breedling users 
            looking for animal transport services.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransporterOnboarding;
